<template>
  <div ref="crudTableFitRoot">
    <v-data-table
      :page="page"
      @update:page="page = $event"
      :headers="headers"
      :items="aiJobs"
      class="elevation-1"
      hide-default-footer
      hide-details="auto"
      :loading="apiLoading"
      :custom-sort="customSort"
      :loading-text="$t('table.loading')"
      :options.sync="tableOptions"
      @update:options="changedOptions"
    >
      <template slot="top">
        <v-toolbar flat class="solvit-crud-table-toolbar">
          <v-toolbar-title style="align-items: center; display: flex">
            <v-icon left>
              mdi-robot-outline
            </v-icon>{{ $t('aiJobTable.title') }}
          </v-toolbar-title>
          <v-divider class="mx-4" inset vertical />
          <v-spacer />
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('aiJobTable.searchLabel')"
            single-line
            hide-details="auto"
          />
          <v-spacer />
        </v-toolbar>
      </template>
      <template slot="item.samplingFlag" slot-scope="{ item }">
        <v-tooltip
          v-if="item.samplingFlag"
          bottom
          max-width="280"
        >
          <template #activator="{ on, attrs }">
            <v-chip
              small
              outlined
              color="info"
              v-bind="attrs"
              v-on="on"
            >
              {{ $t('aiJobTable.samplingChip') }}
            </v-chip>
          </template>
          <span>{{ $t('aiJob.samplingRunTooltip') }}</span>
        </v-tooltip>
        <span v-else>-</span>
      </template>
      <template slot="item.reportName" slot-scope="{ item }">
        <div class="d-flex align-center flex-wrap" style="gap: 6px; min-width: 0;">
          <span class="text-truncate">{{ item.reportName }}</span>
          <v-chip
            v-if="isAiJobRunning(item)"
            x-small
            label
            color="primary"
            outlined
            class="solvit-row-work-badge flex-shrink-0"
          >
            {{ $t('common.workInProgress') }}
          </v-chip>
        </div>
      </template>
      <template slot="item.aIAnalysisDetail" slot-scope="{ item }">
        <v-btn
          :key="item.id"
          outlined
          small
          :class="[
            'solvit-crud-outline-btn',
            { 'solvit-crud-outline-btn--progress-active': isAiJobRunning(item) }
          ]"
          @click="showAIDetailDialog(item)"
        >
          <v-icon left>
            mdi-poll
          </v-icon>
          {{ $t('aiJobTable.col.aIAnalysisDetail') }}
        </v-btn>
      </template>
      <template slot="item.actions" slot-scope="{ item }">
        <v-btn
          :key="item.id"
          outlined
          small
          class="solvit-crud-outline-btn solvit-crud-outline-btn--danger"
          :title="$t('common.delete')"
          @click="deleteItem(item)"
        >
          <v-icon left>
            mdi-delete
          </v-icon>{{ $t('common.delete') }}
        </v-btn>
      </template>
      <template slot="no-data">
        <v-btn outlined class="solvit-crud-outline-btn" @click="initialize">
          {{ $t('common.refresh') }}
        </v-btn>
      </template>
    </v-data-table>
    <div class="text-center pt-2">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="10"
        next-icon="mdi-chevron-right"
        prev-icon="mdi-chevron-left"
      />
    </div>
    <AIDetailDialog
      :aIDetailDialog="aIDetailDialog"
      :current-job-id="currentJobId"
      @close="closeDialog"
    />
  </div>
</template>

<script>
import Api from '../../apis/Api';
import AIDetailDialog from '../dialog/AIDetailDialog.vue';
import { getSortParam } from '../../utils/sortUtils';
import '@/assets/css/styles/table_click.scss';
import { 
  createDefaultTableOptions, 
  buildSortParam, 
  buildPaginationParams, 
  buildApiUrl, 
  transformApiResponse 
} from '../../utils/tableUtils';
import { getStatusText } from '../../constants/progressStatus';
import { formatJobKinds } from '../../utils/aiJobUtils';
import { transformToTableItems } from '../../constants/aiAnalysisJobTableItem';
import { crudTablePageSizeFitMixin } from '@/ui/mixins/crudTablePageSizeFitMixin';

export default {
  name: 'AICrudTable',
  mixins: [crudTablePageSizeFitMixin],
  components: {
    AIDetailDialog
  },
  data: () => ({
    // tableUtils를 사용한 기본 테이블 옵션 생성 (created에서 viewport fit로 덮어씀)
    tableOptions: createDefaultTableOptions(['id'], [true], 10),
    
    valid: true,
    menu: false,
    startTime: '',
    search: '',
    itemsPerPage: 10,
    pageCount: 0,
    paging: true,
    page: 1,
    domain: 'ai',
    aIDetailDialog: false,
    currentJobId: 0,
    processRate: 0,
    aiJobs: [],
    projects: [],
    companies: [],
    editedIndex: -1,
    apiLoading: true
  }),
  computed: {
    headers() {
      void this.$i18n.locale;
      const c = (key) => this.$t(`aiJobTable.col.${key}`);
      return [
        { text: c('id'), value: 'id', sortable: true },
        { text: c('siteName'), value: 'siteName' },
        { text: c('reportName'), value: 'reportName' },
        { text: c('examineDate'), value: 'examineDate' },
        { text: c('jobKinds'), value: 'jobKinds', sortable: false },
        { text: c('samplingFlag'), value: 'samplingFlag', sortable: false },
        { text: c('processStatus'), value: 'processStatus' },
        { text: c('processRate'), value: 'processRate' },
        { text: c('jobRefSeq'), value: 'jobRefSeq', sortable: false },
        { text: c('startTime'), value: 'startTime' },
        { text: c('aIAnalysisDetail'), value: 'aIAnalysisDetail', sortable: false },
        { text: c('actions'), value: 'actions', sortable: false }
      ];
    }
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    search(val) {
      this.searchAIData(val, 1, this.tableOptions.sortBy, this.tableOptions.sortDesc);
    },
    page(val) {
      if (this.isCrudTableFitProgrammaticUpdate()) {
        return;
      }
      this.page = val;
      this.searchAIData(this.search, val, this.tableOptions.sortBy, this.tableOptions.sortDesc);
    }
  },
  created() {
    //changedOptions에서 실행
    // this.initialize();
  },
  mounted() {
    // 초기 정렬 옵션을 명시적으로 설정
    this.tableOptions.sortBy = ['id'];
    this.tableOptions.sortDesc = [true];
  },

  methods: {
    /**
     * 기존의 sort기능을 사용하지 않도록 처리
     */
    customSort(items, _index, _isDesc) {
      return items;
    },
    /**
     * tableUtils를 사용한 AI 데이터 조회
     * @param {string} keyword - 검색 키워드
     * @param {number} page - 페이지 번호
     * @param {Array} sortBy - 정렬 필드
     * @param {Array} sortDesc - 정렬 방향
     */
    searchAIData(keyword, page, sortBy, sortDesc) {
      const request = this.beginCrudTableRequest();
      this.apiLoading = true;
      
        let sortParam = '';
        if (sortBy && sortBy.length > 0) {
          const sortableFields = {
            'id': 'id',
            'startTime': 'createTime',
            'processStatus': 'processStatus',
            'reportName': 'solarExamineReport.reportName',
            'siteName': 'solarExamineReport.site.name'
          };
          
          const sortFields = sortBy.map((field, index) => {
            const direction = (sortDesc && sortDesc[index]) ? 'desc' : 'asc';
            const apiField = sortableFields[field] || field;
            return `${apiField},${direction}`;
          });
          sortParam = sortFields.join(',');
        } else {
          // 기본값: id,desc (AiAnalysisJob 엔티티의 실제 ID 필드명)
          sortParam = 'id,desc';
        }
      
      // 페이지 번호는 1부터 시작하도록 조정
      const currentPage = page || this.page;
      const adjustedPage = currentPage - 1; // API는 0부터 시작

      // AI 분석 데이터 조회 - 가독성 개선: API 호출 바로 위에서 params 구성
      Api.getApiResource(`/${this.domain}`, {
        size: this.itemsPerPage,
        page: adjustedPage,
        sort: sortParam,
        ...(keyword && keyword.trim() && { keyword: keyword.trim() })
      }, { signal: request.signal })
        .then((res) => {
          if (!this.isCrudTableRequestCurrent(request.token)) return;

          const transformedData = transformApiResponse(res.data, (items) => {
            return transformToTableItems(
              items,
              getStatusText,
              this.formatJobKinds,
              this.calculateProcessRateText
            );
          });
          
          this.aiJobs = transformedData.items;
          this.pageCount = transformedData.pageCount;
          this.page = transformedData.page;
        })
        .catch((error) => {
          if (!this.isCrudTableRequestCurrent(request.token)) return;
          console.error('AI 분석 데이터 조회 중 오류 발생:', error);
        })
        .finally(() => {
          if (!this.isCrudTableRequestCurrent(request.token)) return;
          this.apiLoading = false;
          this.finishCrudTableRequest(request.token);
        });
    },

    closeDialog() {
      this.aIDetailDialog = false;
    },
    initialize() {
      this.searchAIData();
    },
    showAIDialog(item) {
      // AI 다이얼로그 표시 로직
    },
    changedOptions(val) {
      /**
       * groupBy : Array, groupDesc : Array, itemsPerPage : Number, multiSort : Boolean, mustSort : Boolean, page : Number, sortBy : Array, sortDesc : Array
       */
      
      // tableOptions는 이미 자동으로 업데이트됨
      // 별도로 변경할 필요 없음
      
      // 초기 로드 시에도 데이터를 가져오기 위해 searchAIData 호출
      this.searchAIData(
        this.search, 
        this.page, 
        this.tableOptions.sortBy, 
        this.tableOptions.sortDesc
      );
    },

    isAiJobRunning(item) {
      return item.processStatusCode === 'ING';
    },
    deleteItem(item) {
      try {
        const index = this.aiJobs.indexOf(item);
        if (this.isAiJobRunning(item)) {
          alert(this.$t('aiJobTable.deleteNotAllowed'));
          return;
        }
        confirm(this.$t('common.confirmDeleteSelected')) &&
          Api.delete(`/${this.domain}/${item.id}`, item).then(() => {
            this.aiJobs.splice(index, 1);
            this.searchAIData(
              this.search,
              this.page,
              this.tableOptions.sortBy,
              this.tableOptions.sortDesc
            );
          });
      } catch (e) {
        if (e.response.resultCode == 406) {
          alert(this.$t('aiJobTable.deleteNotAllowed'));
        }
      }
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
      // eventBus 대신 emit 사용 (부모 컴포넌트에서 처리)
      this.$emit('close-dialog', {
        from: 'AICrudTable',
        open: false
      });
    },

    showAIDetailDialog(item) {
      this.currentJobId = item.id;
      this.aIDetailDialog = true;
    },
    triggerInputFile() {
      this.$refs.siteImageFile.click();
    },
    calculateProcessRateText(targetImageCount, completeImageCount) {
      if (targetImageCount === 0) {
        return "-";
      }
      return Math.round((completeImageCount / targetImageCount) * 100) + "%";
    },
    formatJobKinds
  }
};
</script>

<style scoped>
.v-dialog__content {
  max-height: 100%;
}
.v-dialog:not(.v-dialog--fullscreen) {
  max-height: 100%;
}
.custom-padding {
  padding: 8px 12px !important;
}

.required::after {
  content: "*";
  color: red;
  margin-left: 4px;
}

.hover-primary {
  background: var(--v-surface-base);
}
.hover-primary:hover {
  background: var(--v-primary-base) !important;
}
</style>
